// 相続後リユース候補画面の定数・文言。
// リポジトリの流儀に合わせ、日本語文言はコード内に集約する（i18n ライブラリ不使用）。

import type { IInheritanceReuseFilterCondition } from "@/features/inheritanceReuse/types";

/** 画面ルート（App のルート定義・サイドメニュー href の正本）。 */
export const INHERITANCE_REUSE_PATH = "/inheritance-reuse";

/** 画面タイトル・メニュー表示名（ユーザー向け表示名の正本。内部識別子・ルートは据え置き）。 */
export const INHERITANCE_REUSE_TITLE = "相続後リユース検索";

/** ヒーローヘッダーのサブコピー。 */
export const INHERITANCE_REUSE_SUBTITLE =
  "相続の発生した不動産から、売却・次相続の可能性が高い候補を厳選してご提案します。";

/** 当社が所有者事項を取得済みの物件のみ一覧表示している旨の注記。 */
export const INHERITANCE_REUSE_ACQUIRE_NOTE =
  "検索結果の表示は所有者事項のみです";

/** 売却期待ランクの最大★数。 */
export const SALE_RANK_MAX = 4;

/** 推定値の精度に関するディスクレーマ（注意書き）の要約文。 */
export const INHERITANCE_REUSE_DISCLAIMER_SUMMARY =
  "空き家・居住・距離区分・所有者の高齢/カナ名や、公示価格・用途地域などは推定・参考値です。正確性は保証されません。";

/** ディスクレーマの詳細文（アコーディオンで表示）。具体的な精度数値は出さない。 */
export const INHERITANCE_REUSE_DISCLAIMER_DETAILS: string[] = [
  "空き家フラグ・居住フラグ・距離区分（居住／同市区町村／同都道府県／他県）・所有者の氏名系（高齢推定やカナ名）などは、所有者住所と所在の文字列比較やルールベースによる推定に基づく参考値であり、正確性を保証するものではありません。",
  "公示価格は、当該物件そのものの価格ではなく、近傍の地価公示標準地の単価（円/㎡・直近取得可能な年度）を参考値として表示しています。実勢価格や評価額とは一致しません。",
  "推定土地評価額は、公示価格（近傍標準地の単価）× 推計地積による概算です。地積も推計値を含むため、実勢価格や評価額とは一致しません。",
  "用途地域は国土交通省の公開データ（国土数値情報／都市計画決定GISデータ）をもとに表示しています。データの年度により、最新の都市計画や実際の指定と異なる場合があります。また、物件（所在）の中心点をもとに付与している場合があり、敷地が複数の用途地域にまたがるときなど実際と異なることがあります。",
];

/** ページサイズ選択肢（BE 上限 100 に合わせる）。 */
export const ROWS_PER_PAGE_OPTIONS: number[] = [25, 50, 100];

/** 初期ページサイズ。 */
export const DEFAULT_PAGE_SIZE = 100;

/** 種別セレクトの選択肢（不動産登記取得と同じ英字コードを用いる）。 */
export const REAL_ESTATE_TYPE_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "LAND", label: "土地" },
  { value: "BUILDING", label: "建物" },
  { value: "CONDOMINIUM", label: "区分建物" },
];

/**
 * 距離区分セレクトの選択肢（BE DistanceCategory）。
 * ラベルは CEO 指定の簡潔表記（居住/同市区/同都府県/他県/不明）。
 */
export const DISTANCE_CATEGORY_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: "LIVING", label: "居住" },
  { value: "SAME_CITY", label: "同市区" },
  { value: "SAME_PREFECTURE", label: "同都府県" },
  { value: "OTHER_PREFECTURE", label: "他県" },
  { value: "UNKNOWN", label: "不明" },
];

/** フィルタの初期値。 */
export const INITIAL_FILTER_CONDITION: IInheritanceReuseFilterCondition = {
  prefectureName: "",
  cityName: "",
  realEstateType: "",
  distanceCategory: "",
  acquiredFrom: "",
  acquiredTo: "",
  inheritanceFrom: "",
  inheritanceTo: "",
  buildYearFrom: "",
  buildYearTo: "",
  minEstimatedLandValue: "",
  maxEstimatedLandValue: "",
  minSaleRank: "",
  minInheritanceScore: "",
  isOccupant: false,
  emptyHouse: false,
  maxOwnerCount: "",
};

/**
 * 売却期待度★ランク（1〜4）→ 送信する売却期待「スコア」下限（min_sale_score）の対応。
 * BE constants.py の sale_rank() は 0点=1 / 1〜2点=2 / 3〜4点=3 / 5点以上=4。
 * よって「★N以上」を満たす最小スコアは以下（★1以上は全件＝スコア0以上）。
 */
export const SALE_RANK_TO_MIN_SCORE: Record<number, number> = {
  1: 0,
  2: 1,
  3: 3,
  4: 5,
};

/**
 * DataGrid のソート列（camelCase）→ DRF ordering フィールド（snake_case）の対応。
 * BE types.py ALLOWED_ORDERINGS に載っている 4 フィールドのみサーバソート可能。
 * ここに無い列は列定義側で sortable:false にする（送られない不整合を防ぐ）。
 */
export const SORTABLE_FIELD_TO_ORDERING: Record<string, string> = {
  acquiredDate: "acquired_date",
  inheritanceDate: "inheritance_date",
  saleScore: "sale_score",
  inheritanceScore: "inheritance_score",
};

/** サーバソート可能な列（camelCase）の集合。列定義の sortable 制御に用いる。 */
export const SORTABLE_FIELDS: ReadonlySet<string> = new Set(
  Object.keys(SORTABLE_FIELD_TO_ORDERING)
);
